/**
 * Hook para gerenciar configurações de distribuição automática de grupos
 * 
 * Permite ativar/desativar distribuição, selecionar departamento e 
 * configurar o trigger (primeira mensagem ou toda mensagem).
 * Usa RPC configure_group_distribution para persistir alterações.
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

// Logger específico para distribuição de grupos
const logger = createLogger('useGroupDistribution');

// Tipo de trigger de distribuição
export type DistributionTrigger = 'first_message' | 'every_message';

// Configuração de distribuição do grupo
export interface GroupDistributionConfig {
  autoDistributionEnabled: boolean;
  defaultDepartmentId: string | null;
  distributionTrigger: DistributionTrigger;
  lastDistributedAt: string | null;
}

// Interface do hook
interface UseGroupDistributionReturn {
  config: GroupDistributionConfig | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  updateConfig: (updates: Partial<GroupDistributionConfig>) => Promise<boolean>;
  refetch: () => Promise<void>;
}

/**
 * Hook para gerenciar distribuição de grupos
 * @param groupId - ID do grupo no banco de dados
 */
export function useGroupDistribution(groupId: string | null): UseGroupDistributionReturn {
  const { currentTenant } = useAuth();
  const [config, setConfig] = useState<GroupDistributionConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Busca configuração atual do grupo
   */
  const fetchConfig = useCallback(async () => {
    if (!groupId) {
      setConfig(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Busca via RPC server-side (substitui SELECT direto em chat_groups)
      const { data: rows, error: fetchError } = await supabase.rpc('get_chat_group_safe', {
        p_group_id: groupId,
        p_tenant_hint: currentTenant?.id ?? null,
      });

      if (fetchError) {
        logger.error('Erro ao buscar config de distribuição:', fetchError);
        setError('Erro ao carregar configuração');
        setConfig(null);
        return;
      }

      const data = rows && rows.length > 0 ? rows[0] : null;
      if (!data) {
        setConfig(null);
        return;
      }

      setConfig({
        autoDistributionEnabled: data.auto_distribution_enabled || false,
        defaultDepartmentId: data.default_department_id,
        distributionTrigger: (data.distribution_trigger as DistributionTrigger) || 'first_message',
        lastDistributedAt: data.last_distributed_at,
      });

      logger.info(`Config carregada para grupo ${groupId}:`, {
        enabled: data.auto_distribution_enabled,
        dept: data.default_department_id,
      });
    } catch (err) {
      logger.error('Erro inesperado ao buscar config:', err);
      setError('Erro inesperado');
    } finally {
      setLoading(false);
    }
  }, [groupId, currentTenant?.id]);

  /**
   * Atualiza configuração de distribuição via RPC
   * @param updates - Campos a atualizar
   */
  const updateConfig = useCallback(async (updates: Partial<GroupDistributionConfig>): Promise<boolean> => {
    if (!groupId || !currentTenant?.id) {
      setError('Grupo ou tenant não disponível');
      return false;
    }

    setSaving(true);
    setError(null);

    try {
      // Monta parâmetros para RPC
      const newConfig = {
        ...config,
        ...updates,
      };

      logger.info(`Atualizando config do grupo ${groupId}:`, newConfig);

      const { error: rpcError } = await supabase.rpc('configure_group_distribution', {
        p_group_id: groupId,
        p_department_id: newConfig.defaultDepartmentId || null,
        p_enabled: newConfig.autoDistributionEnabled ?? false,
        p_trigger: newConfig.distributionTrigger || 'first_message',
        p_tenant_hint: currentTenant.id,
      });

      if (rpcError) {
        logger.error('Erro ao salvar config de distribuição:', rpcError);
        setError(rpcError.message || 'Erro ao salvar configuração');
        return false;
      }

      // Atualiza estado local
      setConfig(prev => prev ? { ...prev, ...updates } : null);

      logger.info(`Config atualizada com sucesso para grupo ${groupId}`);
      return true;
    } catch (err) {
      logger.error('Erro inesperado ao atualizar config:', err);
      setError('Erro inesperado');
      return false;
    } finally {
      setSaving(false);
    }
  }, [groupId, currentTenant?.id, config]);

  // Busca config quando muda o grupo
  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  return {
    config,
    loading,
    saving,
    error,
    updateConfig,
    refetch: fetchConfig,
  };
}
